// store/contentSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface ChartHeader {
  id: number;
  name: string;
  photo_url: string;
}

export interface Message {
  id: number;
  sender_id: number;
  receiver_id: number;
  message: string;
  is_read: boolean;
  sent_at: string;
  read_at: any;
}

export interface Chat {
  id: number;
  chatKey: string;
  user_id: number;
  last_message?: string;
  unread_count: number;
  photo_url?: string; // Make these optional if they might not always be present
  name?: string;
  message: string;
  last_message_sent_at: string;
  unread_messages_count: number;
}

interface MessageSlice {
  conversations: any; // Keyed by user ID
  unreadCount: number;
  unreadMessages: any[];
  currentActiveConversation: any;
  unreadChats: Chat[];
  chats: Chat[];
  chartHeader: ChartHeader;
  messages: Message[];
  hasMoreMessages: boolean;
  currentPage: number;
  isLoadingMessages: boolean;
  showChatMessages: boolean;
  chartId: number;
  receiverId: number;
  
}

const initialState: MessageSlice = {
  conversations: {},
  unreadCount: 0,
  unreadMessages: [],
  currentActiveConversation: null,
  unreadChats: [],
  chartHeader: {
    id: 0,
    name: "",
    photo_url: "",
  },
  chats: [],
  messages: [],
  hasMoreMessages: true,
  currentPage: 1,
  isLoadingMessages: false,
  showChatMessages: false,
  chartId: 0,
  receiverId: 0,
};

const messageSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    addMessage: (state, action) => {
      const { senderId, content, timestamp } = action.payload;

      // If conversation doesn't exist, create it
      if (!state.conversations[senderId]) {
        state.conversations[senderId] = [];
      }

      // Add new message to conversation
      state.conversations[senderId].push({
        senderId,
        content,
        timestamp,
      });

      // Increment unread count
      state.unreadCount += 1;
    },
    setActiveConversation: (state, action) => {
      state.currentActiveConversation = action.payload;

      // Reset unread count for this conversation
      if (action.payload) {
        state.conversations[action.payload].forEach(
          (msg: any) => (msg.read = true)
        );
      }
    },
    setShowChatMessages: (state, action) => {
      state.showChatMessages = action.payload;
    },
    setUnreadMessages: (state, action) => {
      state.unreadMessages = action.payload;
    },
    setChat: (state, action) => {
      // When setting chats, we maintain the existing order
      state.chats = action.payload;
    },
    addOrUpdateChat: (state, action) => {
      const newChat = action.payload;
      // First, check if this chat already exists by looking for matching id
      const existingChatIndex = state.chats.findIndex(
        (chat) => chat.id === newChat.id
      );

      if (existingChatIndex !== -1) {
        // If chat exists, remove it from its current position
        const existingChat = state.chats[existingChatIndex];
        state.chats.splice(existingChatIndex, 1);
        // Place it at the beginning of the array, preserving its existing data
        state.chats.unshift({
          ...existingChat,
          // Only update these fields if they exist in newChat
          ...(newChat.photo_url && { photo_url: newChat.photo_url }),
          ...(newChat.name && { name: newChat.name }),
        });
      } else {
        // If chat doesn't exist, add new chat to the beginning
        state.chats.unshift(newChat);
      }
    },
    clearUnreadCount: (state) => {
      state.unreadCount = 0;
    },
    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },
    setChartHeader: (state, action: PayloadAction<ChartHeader>) => {
      state.chartHeader = action.payload;
    },
    // Add unread messages
    addUnreadMessage: (state, action: PayloadAction<MessagePort>) => {
      state.unreadMessages.push(action.payload);
    },

    // Mark specific chats as unread
    markUnreadChats: (state, action: PayloadAction<Chat[]>) => {
      state.unreadChats = action.payload;
    },

    // Mark a chat as read
    markChatAsRead: (state, action: PayloadAction<number>) => {
      state.unreadChats = state.unreadChats.filter(
        (chat) => chat.id !== action.payload
      );

      // Remove read messages
      state.unreadMessages = state.unreadMessages.filter(
        (msg) => msg.sender_id !== action.payload
      );
    },
    setMessages: (state, action: PayloadAction<Message[]>) => {
      state.messages = action.payload;
    },

    appendMessages: (state, action: PayloadAction<Message[]>) => {
      // Add older messages and then sort all messages by `sent_at`
      state.messages = [...action.payload, ...state.messages].sort(
        (a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime()
      );
    },

    addNewMessage: (state, action: PayloadAction<Message>) => {
      // Add new message to the end of the array
      state.messages.push(action.payload);
    },

    setHasMoreMessages: (state, action: PayloadAction<boolean>) => {
      state.hasMoreMessages = action.payload;
    },

    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },

    setIsLoadingMessages: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMessages = action.payload;
    },
    // update chat last message and time with nottification data
    // updateChatMessages: (state, action) => {
    //   const messages = action.payload;

    //   // Create a map to group messages by chat
    //   const messagesByChat = new Map();

    //   // Group messages by their respective chats
    //   messages.forEach((msg: any) => {
    //     const { sender_id, receiver_id, message, sent_at, sender_name } = msg;

    //     // Create a unique key for the chat (combine sender and receiver IDs)
    //     const chatKey = [sender_id, receiver_id].sort().join("-");

    //     if (!messagesByChat.has(chatKey)) {
    //       messagesByChat.set(chatKey, {
    //         messages: [],
    //         sender_id,
    //         receiver_id,
    //         sender_name,
    //         latest_sent_at: sent_at,
    //       });
    //     }

    //     const chatData = messagesByChat.get(chatKey);
    //     chatData.messages.push(message);

    //     // Update latest sent_at if this message is newer
    //     if (new Date(sent_at) > new Date(chatData.latest_sent_at)) {
    //       chatData.latest_sent_at = sent_at;
    //     }
    //   });

    //   // Process each chat's messages
    //   messagesByChat.forEach((chatData, chatKey) => {
    //     const {
    //       sender_id,
    //       receiver_id,
    //       messages,
    //       sender_name,
    //       latest_sent_at,
    //     } = chatData;

    //     // Find the chat to update
    //     const chatIndex = state.chats.findIndex((chat) => {
    //       const isUserInChat =
    //         chat.user_id === sender_id || chat.user_id === receiver_id;
    //       return isUserInChat;
    //     });

    //     if (chatIndex !== -1) {
    //       // Remove the chat from its current position
    //       const chatToUpdate = state.chats[chatIndex];
    //       state.chats.splice(chatIndex, 1);

    //       // Update the chat with the latest message and accumulated unread count
    //       const updatedChat = {
    //         ...chatToUpdate,
    //         last_message: messages[messages.length - 1], // Use the most recent message
    //         message: messages[messages.length - 1],
    //         // Increment unread count by the number of messages if user is receiver
    //         unread_count:
    //           chatToUpdate.user_id === receiver_id
    //             ? chatToUpdate.unread_count + messages.length
    //             : chatToUpdate.unread_count,
    //         // Update name if sender_name is provided and user_id matches sender
    //         ...(sender_name && chatToUpdate.user_id === sender_id
    //           ? { name: sender_name }
    //           : {}),
    //       };

    //       // Add the updated chat to the beginning of the array
    //       state.chats.unshift(updatedChat);
    //     }
    //   });
    // },
    updateChatMessages: (state, action) => {
      const messages = action.payload;

      // Create a map to group messages by chat
      const messagesByChat = new Map();

      // Group messages by their respective chats
      messages.forEach((msg: any) => {
        const {
          sender_id,
          receiver_id,
          message,
          last_message_sent_at,
          sender_name,
        } = msg;

        // Create a unique key for the chat
        const chatKey = [sender_id, receiver_id].sort().join("-");

        if (!messagesByChat.has(chatKey)) {
          messagesByChat.set(chatKey, {
            messages: [],
            sender_id,
            receiver_id,
            sender_name,
            last_message_sent_at, // Use server's timestamp field
            chatKey, // Store chatKey inside the object
          });
        }

        const chatData = messagesByChat.get(chatKey);
        chatData.messages.push(message);

        // Update last_message_sent_at if this message is newer
        if (
          new Date(last_message_sent_at) >
          new Date(chatData.last_message_sent_at)
        ) {
          chatData.last_message_sent_at = last_message_sent_at;
        }
      });

      // Process each chat's messages
      messagesByChat.forEach((chatData, chatKey) => {
        const {
          sender_id,
          receiver_id,
          messages,
          sender_name,
          last_message_sent_at,
        } = chatData;

        // Find the chat using chatKey
        const chatIndex = state.chats.findIndex(
          (chat) =>  chat.id === state.chartId || chat.chatKey === chatKey 
        );

        if (chatIndex !== -1) {
          // Remove the chat from its current position
          const chatToUpdate = state.chats[chatIndex];
          state.chats.splice(chatIndex, 1);

          // Update the chat with the latest message and accumulated unread count
          const updatedChat = {
            ...chatToUpdate,
            last_message: messages[messages.length - 1], // Use the most recent message
            message: messages[messages.length - 1],
            chatKey, // Ensure chatKey is stored in chat object
            unread_count:
              chatToUpdate.user_id === receiver_id
                ? chatToUpdate.unread_count + messages.length
                : chatToUpdate.unread_count,
            last_message_sent_at, // Ensure latest timestamp is updated
            ...(sender_name && chatToUpdate.user_id === sender_id
              ? { name: sender_name }
              : {}),
          };

          // Add the updated chat to the beginning of the array
          state.chats.unshift(updatedChat);
        } else {
          // If chat does not exist, create a new entry
          state.chats.unshift({
            id: 1,
            unread_messages_count: 0,
            chatKey, // Store chatKey in new chat object
            user_id: receiver_id, // Assuming the current user is the receiver
            last_message: messages[messages.length - 1],
            message: messages[messages.length - 1],
            unread_count: messages.length, // First message(s) in chat
            last_message_sent_at, // Server's timestamp
            name: sender_name || "", // Use sender's name if available
          });
        }
      });
    },

    clearChatUnreadCount: (state, action) => {
      const { userId, chatUserId } = action.payload;

      // Find the chat where user_id matches chatUserId
      const chatIndex = state.chats.findIndex((chat) => chat.id === chatUserId);

      if (chatIndex !== -1) {
        // Reset unread count for this chat
        state.chats[chatIndex] = {
          ...state.chats[chatIndex],
          unread_messages_count: 0,
        };
      }
    },
    setChartId: (state, action: PayloadAction<number>) => {
      state.chartId = action.payload;
    },
    setReceiverId: (state, action: PayloadAction<number>) => {
      state.receiverId = action.payload;
    },

    updateChatMessage: (state, action) => {
      const {
        sender_id,
        receiver_id,
        message,
        last_message_sent_at,
        sender_name,
      } = action.payload;

      // Generate a unique key for the chat (ensuring consistent ordering)
      const chatKey = [sender_id, receiver_id].sort().join("-");

       // Find the chat using chatKey
       const chatIndex = state.chats.findIndex(
        (chat) =>  chat.id === state.chartId || chat.chatKey === chatKey 
      );

      if (chatIndex !== -1) {
        // Remove the chat from its current position
        const chatToUpdate = state.chats[chatIndex];
        state.chats.splice(chatIndex, 1);

        // Create updated chat object
        const updatedChat = {
          ...chatToUpdate,
          last_message: message, // Update last message
          message: message,
          chatKey, // Ensure chatKey is stored
          unread_count:
            chatToUpdate.user_id === receiver_id
              ? chatToUpdate.unread_count + 1
              : chatToUpdate.unread_count,
          last_message_sent_at, // Use server's timestamp field
          ...(sender_name && chatToUpdate.user_id === sender_id
            ? { name: sender_name }
            : {}),
        };

        // Move chat to the top of the list
        state.chats.unshift(updatedChat);
      }
    },
    receiveWebSocketMessage: (state, action: PayloadAction<Message>) => {
      const newMessage = action.payload;

      // Check if the message already exists (to avoid duplicates)
      const existingMessage = state.messages.find(
        (msg) => msg.id === Number(newMessage.id)
      );

      if (existingMessage) return;
      const completeMessage = {
        id: Number(newMessage.id),
        is_read: false,
        sender_id: newMessage.sender_id,
        receiver_id: newMessage.receiver_id,
        receiver_name: "",
        read_at: null,
        message: newMessage.message,
        sent_at: newMessage.sent_at,
        sender_name: "",
      };

      // Add new message to the chat messages
      state.messages.push(completeMessage);

      // update chat
      // Generate a unique key for the chat (ensuring consistent ordering)
      const chatKey = [newMessage?.sender_id, newMessage?.receiver_id]
        .sort()
        .join("-");

      // Find the chat by matching its chatKey
      const chatIndex = state.chats.findIndex(
        (chat) =>  chat.id === state.chartId || chat.chatKey === chatKey 
      );
      

      if (chatIndex !== -1) {
        // Remove the chat from its current position
        const chatToUpdate = state.chats[chatIndex];
        state.chats.splice(chatIndex, 1);

        // Create updated chat object
        const updatedChat = {
          ...chatToUpdate,
          last_message: newMessage.message, // Update last message
          message: newMessage?.message,
          chatKey, // Ensure chatKey is stored
          unread_count:
            chatToUpdate.user_id === newMessage?.receiver_id
              ? chatToUpdate.unread_count + 1
              : chatToUpdate.unread_count,
          last_message_sent_at: newMessage.sent_at, // Use server's timestamp field
        };

        // Move chat to the top of the list
        state.chats.unshift(updatedChat);
      }
    },
  },
});

export const {
  addMessage,
  setChat,
  addOrUpdateChat,
  setActiveConversation,
  setUnreadCount,
  setChartHeader,
  clearUnreadCount,
  markChatAsRead,
  markUnreadChats,
  addUnreadMessage,
  setMessages,
  setShowChatMessages,
  appendMessages,
  addNewMessage,
  setHasMoreMessages,
  setCurrentPage,
  setIsLoadingMessages,
  updateChatMessages,
  updateChatMessage,
  receiveWebSocketMessage,
  setChartId,
  setReceiverId,
  clearChatUnreadCount,
} = messageSlice.actions;

export default messageSlice.reducer;
